import { type ClubDef, clubsInOrder } from "@clubap/shared";
import { clubLogoUrl } from "../social/api.js";

interface Props {
  clubs: readonly string[];
  /** Con el nombre completo al lado. Cabe en un perfil, no en una lista. */
  withNames?: boolean;
}

/**
 * Las insignias de club de alguien.
 *
 * Los ids desconocidos se descartan en vez de dibujarse crudos: el catálogo puede
 * encoger, y una cuenta vieja seguiría teniendo la fila en la base.
 */
export function ClubBadges({ clubs, withNames = false }: Props) {
  const defs = clubsInOrder(clubs);
  if (defs.length === 0) return null;

  return (
    <span className="badges">
      {defs.map((club) => (
        <span
          className={`badge${withNames ? " badge--named" : ""}`}
          key={club.id}
          style={{ color: club.color, borderColor: club.color }}
          title={club.name}
        >
          <ClubMark club={club} />
          {withNames && <span className="badge__name">{club.name}</span>}
        </span>
      ))}
    </span>
  );
}

/**
 * La marca de un club: su logo, o sus iniciales mientras no tenga.
 *
 * El logo va SIEMPRE dentro de un `<img>`, y eso no es una elección de estilo: los logos
 * los sube cualquier jugador y se admiten SVG, que son código. Incrustado en el documento
 * un SVG ejecuta lo que traiga; dentro de un `<img>` el navegador lo trata como imagen y
 * no ejecuta nada. El servidor además lo sanea al guardarlo — hacen falta las dos cosas,
 * y ésta es la mitad que se olvida.
 *
 * Las siglas no son un "cargando": son el estado normal de un club recién fundado, y
 * además son como se le dice al club en el campus, así que se dibujan igual de acabadas
 * que el logo.
 */
export function ClubMark({ club, size }: { club: ClubDef; size?: number }) {
  const style = size ? { width: size, height: size } : undefined;

  if (club.logoVersion === 0) {
    return (
      <span className="badge__siglas" style={style} aria-hidden="true">
        {club.code}
      </span>
    );
  }

  return (
    <img
      className="badge__logo"
      style={style}
      src={clubLogoUrl(club)}
      alt=""
      /* El logo es decorado: el nombre del club ya está en el `title` del contenedor y,
         cuando toca, escrito al lado. Un `alt` con el nombre lo haría decir dos veces. */
      aria-hidden="true"
      draggable={false}
    />
  );
}
